/**
 * Custom Hook: useDynamicRoutes
 * Generates and manages dynamic routes based on user RBAC permissions
 *
 * Usage: const { routes, isLoading, allowedRoutes } = useDynamicRoutes();
 *
 * NOTE: The permissions store already merges API data with default config,
 * so routePermissions here is always the merged result.
 */

import { useEffect, useState } from 'react';
import { useRoutePermissions } from 'modules/user_management/store/permissionsStore';
import { generateDynamicRoutes, getAllowedRoutes } from './dynamicRouter';

export const useDynamicRoutes = () => {
  const { routePermissions, isLoading, lastUpdated, fetchRoutePermissions } = useRoutePermissions();
  const [routes, setRoutes] = useState([]);
  const [allowedRoutes, setAllowedRoutes] = useState([]);
  const [routesReady, setRoutesReady] = useState(false);

  // Trigger fetch on mount (skip /reset-password: logged-out /profile 401s -> /login).
  useEffect(() => {
    if (window.location.pathname.startsWith('/reset-password')) return;
    fetchRoutePermissions();
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // routePermissions is already merged with defaults by the store
    const permissionsToUse = routePermissions;

    if (!permissionsToUse || Object.keys(permissionsToUse).length === 0) {
      setRoutesReady(true);
      return;
    }

    try {
      const generatedRoutes = generateDynamicRoutes(permissionsToUse);
      const allowed = getAllowedRoutes(permissionsToUse);

      setRoutes(generatedRoutes);
      setAllowedRoutes(allowed);
      setRoutesReady(true);
    } catch (error) {
      setRoutes([]);
      setAllowedRoutes([]);
      setRoutesReady(true);
    }
  }, [routePermissions, isLoading, lastUpdated]);

  // Report loading until BOTH API is done AND routes are generated
  const effectiveLoading = isLoading || !routesReady;

  return {
    routes,
    isLoading: effectiveLoading,
    allowedRoutes,
    permissionsLoaded: !effectiveLoading
  };
};

export default useDynamicRoutes;
